import React, { useState } from "react";
import { Col, Container, Row, Spinner, Modal } from "reactstrap";
import { getInstructionsConfig } from "./instructions";
import ScreenRecordHelp from "../../components/ScreenRecordHelp";
import WalmartInstructions from "./WalmartInstructions";
import GoogleActivityInstructions from "./YoutubeInstructions";
import RobloxInstructions from "./RobloxInstructions";
import PerplexityInstructions from "./PerplexityInstructions";
import ChatGPTInstructions from "./ChatGPTInstructions";

const queryString = require("query-string");

function DataTypeInstructions({
  dataType,
  platform,
  lang,
  showUploader,
  isWebClient = false,
  collectionMode,
}) {
  // special case for backwards compatibility
  if (dataType === "youtube-watch-history") {
    dataType = "youtube_watch_history";
  }
  if (dataType === "google_activity") {
    dataType = "google_activity_history";
  }
  if (dataType === "battery_stats") {
    dataType = "ios_battery_usage_history";
  }

  const [hasTappedOnStart, setHasTappedOnStart] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const [hasStoppedRecording, setHasStoppedRecording] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);

  const [extensionDataFetched, setExtensionDataFetched] = useState(false);

  const getString = (name, lang) => {
    const strings = {
      choose_file: {
        en: "Choose file",
        pt: "Escolher arquivo",
        es: "Elija el archivo",
        id: "Pilih File",
        it: "Scegli il file",
        fr: "Choisir le fichier",
        de: "Datei wählen",
      },
      submit: {
        en: "Submit",
        pt: "Enviar",
        es: "Enviar",
        id: "Kirim",
        it: "Invia",
        fr: "Envoyer",
        de: "Senden",
      },
      finished_the_steps: {
        en: "I have finished the steps",
        pt: "Terminei os passos",
        es: "He terminado los pasos",
        id: "Saya telah menyelesaikan langkah-langkahnya",
        it: "Ho finito i passaggi",
        fr: "j'ai fini les étapes",
        de: "Ich habe die Schritte abgeschlossen",
      },
      instructions_not_found: {
        en: "Sorry, those instructions were not found.",
        pt: "Desculpe, essas instruções não foram encontradas.",
        es: "Lo sentimos, no se encontraron esas instrucciones.",
        id: "Maaf, petunjuk tersebut tidak ditemukan.",
        it: "Spiacenti, le istruzioni non sono state trovate.",
        fr: "Désolé, ces instructions n'ont pas été trouvées.",
        de: "Leider wurden diese Anweisungen nicht gefunden.",
      },
      consent_description: {
        en: "Data submitted for this task may be considered personal or sensitive in nature and your participation is strictly voluntary. Any data submitted will be used for research purposes only and will only be presented in a de-identified or aggregate form that cannot be linked back to you. All data is collected and processed as per our Privacy Policy and adhere to GDPR (General Data Protection Regulation).",
        pt: "Os dados enviados para esta tarefa podem ser considerados de natureza pessoal ou sensível e sua participação é estritamente voluntária. Quaisquer dados enviados serão usados ​​apenas para fins de pesquisa e serão apresentados apenas em um formulário não identificado ou agregado que não pode ser vinculado a você. Todos os dados são coletados e processados ​​de acordo com nossa Política de Privacidade e aderem ao GDPR (Regulamento Geral de Proteção de Dados).",
        es: "Los datos enviados para esta tarea pueden ser considerados de carácter personal o sensible y su participación es estrictamente voluntaria. Todos los datos enviados se utilizarán solo con fines de investigación y solo se presentarán en una forma anónima o agregada que no se puede vincular a usted. Todos los datos se recopilan y procesan de acuerdo con nuestra Política de privacidad y se adhieren a GDPR (Reglamento general de protección de datos).",
        id: "Data yang dikirimkan untuk tugas ini dapat dianggap bersifat pribadi atau sensitif dan partisipasi Anda sepenuhnya bersifat sukarela. Setiap data yang dikirimkan hanya akan digunakan untuk tujuan penelitian dan hanya akan disajikan dalam bentuk agregat atau de-identifikasi yang tidak dapat ditautkan kembali kepada Anda. Semua data dikumpulkan dan diproses sesuai dengan Kebijakan Privasi kami dan mematuhi GDPR (Peraturan Perlindungan Data Umum).",
        it: "I dati inviati per questa attività possono essere considerati di natura personale o sensibile e la tua partecipazione è strettamente volontaria. Tutti i dati inviati verranno utilizzati esclusivamente a fini di ricerca e saranno presentati solo in forma anonima o aggregata che non può essere ricollegata all'utente. Tutti i dati vengono raccolti ed elaborati secondo la nostra Informativa sulla privacy e aderiscono al GDPR (Regolamento generale sulla protezione dei dati).",
        fr: "Les données soumises pour cette tâche peuvent être considérées comme personnelles ou de nature sensible et votre participation est strictement volontaire. Toutes les données soumises seront utilisées uniquement à des fins de recherche et ne seront présentées que sous une forme anonymisée ou agrégée qui ne peut pas être liée à vous. Toutes les données sont collectées et traitées conformément à notre politique de confidentialité et respectent le RGPD (Règlement général sur la protection des données).",
        de: "Die für diese Aufgabe übermittelten Daten können als personenbezogen oder vertraulich angesehen werden und Ihre Teilnahme ist absolut freiwillig. Alle übermittelten Daten werden nur zu Forschungszwecken verwendet und nur in anonymisierter oder aggregierter Form präsentiert, die keinen Rückschluss auf Sie zulässt. Alle Daten werden gemäß unserer Datenschutzrichtlinie und im Einklang mit der DSGVO (Datenschutz-Grundverordnung) erhoben und verarbeitet.",
      },
      consent_checkbox: {
        en: "I provide Measure with my consent to process and use special category data for these purposes",
        pt: "Forneço à Medida meu consentimento para processar e usar dados de categoria especial para esses fins",
        es: "Proporciono a Measure mi consentimiento para procesar y utilizar datos de categorías especiales para estos fines",
        id: "Saya memberikan Tindakan dengan persetujuan saya untuk memproses dan menggunakan data kategori khusus untuk tujuan ini",
        it: "Fornisco a Measure il mio consenso al trattamento e all'utilizzo di dati di categorie speciali per questi scopi",
        fr: "Je donne à Measure mon consentement pour traiter et utiliser des données de catégorie spéciale à ces fins",
        de: "Ich erteile Measure meine Einwilligung zur Verarbeitung und Nutzung von Daten besonderer Kategorie für diese Zwecke",
      },
      record_button: {
        en: "Start Recording",
        es: "Empezar a grabar",
        pt: "Comece a gravar",
        id: "Mulai merekam",
        it: "Inizia a registrare",
        fr: "Commencer l'enregistrement",
        de: "Starte die Aufnahme",
      },
      choose_file_video: {
        en: "Choose video",
        es: "Elige vídeo",
        pt: "Escolha o vídeo",
        id: "Pilih video",
        it: "Scegli video",
        fr: "Choisissez la vidéo",
        de: "Video auswählen",
      },
      choose_file_image: {
        en: "Choose image",
        es: "Elige imagen",
        pt: "Escolha a imagem",
        id: "Pilih gambar",
        it: "Scegli l'immagine",
        fr: "Choisir une image",
        de: "Bild auswählen",
      },
      choose_file_start: {
        en: "Start",
        es: "Comenzar",
        pt: "Começar",
        id: "Awal",
        it: "Inizio",
        fr: "Commencer",
        de: "Start",
      },
    };
    return strings[name][lang] ? strings[name][lang] : strings[name].en;
  };

  const parsed = queryString.parse(window.location.search);

  // show custom instructions when "custom" is set
  let customAppName = "";
  let customDataDescription = "";
  let customLogoURL = "";
  const showSimpleInstructions = parsed.simple ? parsed.simple === "1" : false;
  const isCustomApp = parsed.custom === "1";
  if (isCustomApp) {
    customAppName = parsed.app; // e.g. YouTube
    customDataDescription = parsed.thing; // e.g. watch history
    customLogoURL = parsed.logo;
  }

  // a lazy default here in case it doesn't get set
  if (!lang) {
    lang = "en";
  }

  window.showStartRecording = () => {
    setHasStartedRecording(false);
    setHasTappedOnStart(false);
    setHasStoppedRecording(false);
    setIsFinished(false);
  };

  window.showStopRecording = () => {
    setHasStartedRecording(true);
    setHasTappedOnStart(false);
    setHasStoppedRecording(false);
    setIsFinished(false);
  };

  window.uncheckFinishedCheckbox = () => {
    setIsFinished(false);
  };

  window.showUpload = () => {
    setHasStartedRecording(true);
    setHasTappedOnStart(false);
    setHasStoppedRecording(true);
    setIsFinished(false);
  };

  window.isFinished = isFinished;

  const [videoModal, setVideoModal] = useState(false);
  const toggleVideoModal = () => setVideoModal(!videoModal);

  const instructions = getInstructionsConfig(dataType, platform, lang, {
    appName: customAppName,
    dataDescription: customDataDescription,
    logoURL: customLogoURL,
    showSimpleInstructions: showSimpleInstructions,
    collectionMode,
  });

  if (!instructions) {
    return (
      <p className="retro-instructions-error">
        {getString("instructions_not_found", lang)}
      </p>
    );
  }

  // if there is a video URL, then we'll show a video
  let showVideo = !!instructions.video_url;
  if (isWebClient && !instructions.video_show_web_client) {
    showVideo = false;
  }

  // the built-in screen recorder
  const useInAppScreenRecorder =
    instructions.capture_type === "video" &&
    instructions.use_in_app_screen_recorder &&
    !isWebClient;

  // pick a summary based on the video settings
  let summaryHtml = instructions.summary_html;
  if (!showVideo && instructions.summary_no_video_html) {
    summaryHtml = instructions.summary_no_video_html;
  }

  // we have to set the height in JavaScript because viewport height is weird on mobile
  const videoHeight = isWebClient ? 400 : window.innerHeight - 100;

  const chooseButtonText = {
    video: getString("choose_file_video", lang),
    image: getString("choose_file_image", lang),
    file: getString("choose_file", lang),
    twitter: getString("choose_file_start", lang),
    android_screen_time_stats: getString("choose_file_start", lang),
    google_takeout_data_portability: getString("choose_file_start", lang),
  }[instructions.capture_type];

  const fetchButtonText = {
    localStorage: getString("submit", lang),
  }[instructions.file_source];

  const buttonText =
    instructions.file_source === "localStorage"
      ? fetchButtonText
      : chooseButtonText;

  const isActionButtonEnabled = () => {
    if (instructions.instructions_type === "component") {
      return (
        extensionDataFetched &&
        (!instructions.require_consent ||
          (instructions.require_consent && hasConsented))
      );
    }
    return (
      isFinished &&
      (!instructions.require_consent ||
        (instructions.require_consent && hasConsented))
    );
  };
  return (
    <Container fluid="sm" className="retro-instructions">
      <Row>
        <Col>
          {videoModal && (
            <button
              type="button"
              className="video-close-button"
              aria-label="Close"
              onClick={toggleVideoModal}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          )}

          <div className="data-type-logo">
            <img
              alt={instructions.data_type_description}
              src={instructions.logo}
            />
          </div>
          <div
            className="summary"
            dangerouslySetInnerHTML={{ __html: summaryHtml }}
          />

          {showVideo && (
            <>
              {isWebClient && (
                <div className="video-container">
                  <video
                    controls
                    className="instructions-video"
                    height={videoHeight}
                    poster={instructions.video_poster}
                  >
                    <source src={instructions.video_url} type="video/mp4" />
                    Your browser does not support videos, sorry.
                  </video>
                </div>
              )}

              {!isWebClient && (
                <>
                  <div className="video-thumbnail">
                    <img
                      alt="Video instructions"
                      src={instructions.video_thumbnail}
                      onClick={toggleVideoModal}
                    />
                  </div>

                  <Modal
                    className="instructions-video-modal"
                    centered
                    isOpen={videoModal}
                    toggle={toggleVideoModal}
                  >
                    <div className="modal-video-container">
                      <video
                        controls
                        className="instructions-video"
                        height={videoHeight}
                        poster={instructions.video_poster}
                      >
                        <source src={instructions.video_url} type="video/mp4" />
                        Your browser does not support videos, sorry.
                      </video>
                    </div>
                  </Modal>
                </>
              )}
            </>
          )}

          <div
            className="summary"
            dangerouslySetInnerHTML={{ __html: instructions.after_video_html }}
          />

          {instructions.capture_type === "video" &&
            !instructions.hide_help_link && (
              <ScreenRecordHelp
                lang={lang}
                platform={platform}
                isWebClient={isWebClient}
              />
            )}

          {instructions.steps && (
            <div className="steps">
              {instructions.steps.map((step, index) => (
                <React.Fragment key={index}>
                  {step.step_type === "numbered_step" && (
                    <div className="step">
                      <div className="step-number">{step.number}</div>
                      <div
                        className="step-description"
                        dangerouslySetInnerHTML={{
                          __html: step.description_html,
                        }}
                      />
                    </div>
                  )}
                  {step.step_type === "emoji_step" && (
                    <div className="step emoji-step">
                      <div className="step-number">{step.emoji}</div>
                      <div
                        className="step-description"
                        dangerouslySetInnerHTML={{
                          __html: step.description_html,
                        }}
                      />
                    </div>
                  )}
                  {step.step_type === "centered_image" && (
                    <div className="step centered-image-step">
                      <img
                        src={step.image_url}
                        width={step.image_width}
                        alt=""
                      />
                    </div>
                  )}
                  {step.step_type === "content_step" && (
                    <div
                      className="step content-step"
                      dangerouslySetInnerHTML={{ __html: step.content_html }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {instructions.multiple_steps && (
            <div>
              {instructions.multiple_steps.map((step_set, s_index) => (
                <React.Fragment key={s_index}>
                  <details>
                    <summary>{step_set.title}</summary>
                    <div className="steps">
                      {step_set.steps.map((step, index) => (
                        <React.Fragment key={index}>
                          {step.step_type === "numbered_step" && (
                            <div className="step">
                              <div className="step-number">{step.number}</div>
                              <div
                                className="step-description"
                                dangerouslySetInnerHTML={{
                                  __html: step.description_html,
                                }}
                              />
                            </div>
                          )}
                          {step.step_type === "emoji_step" && (
                            <div className="step emoji-step">
                              <div className="step-number">{step.emoji}</div>
                              <div
                                className="step-description"
                                dangerouslySetInnerHTML={{
                                  __html: step.description_html,
                                }}
                              />
                            </div>
                          )}
                          {step.step_type === "centered_image" && (
                            <div className="step centered-image-step">
                              <img
                                src={step.image_url}
                                width={step.image_width}
                                alt=""
                              />
                            </div>
                          )}
                          {step.step_type === "content_step" && (
                            <div
                              className="step content-step"
                              dangerouslySetInnerHTML={{
                                __html: step.content_html,
                              }}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </details>
                </React.Fragment>
              ))}
            </div>
          )}

          {instructions.after_steps_html && (
            <div
              dangerouslySetInnerHTML={{
                __html: instructions.after_steps_html,
              }}
            />
          )}

          {instructions.part_1_instructions_link && (
            <p className="part-1-instructions-link">
              If you haven't requested the file yet,{" "}
              <a href={instructions.part_1_instructions_link} target="_blank">
                click here to see instructions
              </a>
              .
            </p>
          )}

          {instructions.instructions_type === "component" &&
            instructions.data_type === "walmart_order_history" && (
              <WalmartInstructions
                onDataFetched={(result) => {
                  setExtensionDataFetched(result.dataFresh);
                }}
              />
            )}

          {instructions.instructions_type === "component" &&
            instructions.data_type === "youtube_data_upload" && (
              <GoogleActivityInstructions
                onDataFetched={(result) => {
                  setExtensionDataFetched(result.dataFresh);
                }}
              />
            )}

          {instructions.instructions_type === "component" &&
            instructions.data_type === "roblox_transaction_history" && (
              <RobloxInstructions
                onDataFetched={(result) => {
                  setExtensionDataFetched(result.dataFresh);
                }}
              />
            )}

          {instructions.instructions_type === "component" &&
            instructions.data_type === "perplexity_ai_history" && (
              <PerplexityInstructions
                onDataFetched={(result) => {
                  setExtensionDataFetched(result.dataFresh);
                }}
              />
            )}

          {instructions.instructions_type === "component" &&
            instructions.data_type === "chatgpt_ad_history" && (
              <ChatGPTInstructions
                onDataFetched={(result) => {
                  setExtensionDataFetched(result.dataFresh);
                }}
              />
            )}
          {instructions.require_consent && (
            <React.Fragment>
              <div className="consent-box">
                {instructions.consent_description_html && (
                  <div
                    className="consent-description"
                    dangerouslySetInnerHTML={{
                      __html: instructions.consent_description_html,
                    }}
                  />
                )}
                {!instructions.consent_description_html && (
                  <div className="consent-description">
                    <p>{getString("consent_description", lang)}</p>
                  </div>
                )}
                <div className="steps no-background-or-shadow">
                  <div className="step checkbox-step top-aligned-checkbox-step consent-checkbox-step">
                    <div className="step-number">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="consent-checkbox"
                          checked={hasConsented}
                          onChange={() => setHasConsented(!hasConsented)}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="consent-checkbox"
                        />
                      </div>
                    </div>
                    <div className="step-description">
                      {instructions.consent_checkbox_text && (
                        <label htmlFor="consent-checkbox">
                          {instructions.consent_checkbox_text}
                        </label>
                      )}
                      {!instructions.consent_checkbox_text && (
                        <label htmlFor="consent-checkbox">
                          {getString("consent_checkbox", lang)}
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}

          {["video", "image", "file"].includes(instructions.capture_type) && (
            <React.Fragment>
              {!useInAppScreenRecorder && (
                <React.Fragment>
                  {instructions.instructions_type !== "component" && (
                    <div className="steps no-background-or-shadow">
                      <div className="step checkbox-step">
                        <div className="step-number">
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="finished-checkbox"
                              checked={isFinished}
                              onChange={() => setIsFinished(!isFinished)}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="finished-checkbox"
                            />
                          </div>
                        </div>
                        <div className="step-description">
                          {instructions.finished_steps_text && (
                            <label htmlFor="finished-checkbox">
                              {instructions.finished_steps_text}
                            </label>
                          )}
                          {!instructions.finished_steps_text && (
                            <React.Fragment>
                              <label htmlFor="finished-checkbox">
                                {getString("finished_the_steps", lang)}
                              </label>
                            </React.Fragment>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="action-button">
                    <a
                      className={
                        "btn btn-primary " +
                        (isActionButtonEnabled() ? "" : "disabled")
                      }
                      href={isWebClient ? "#" : "app://job-engine/next"}
                      onClick={showUploader}
                    >
                      {buttonText}
                    </a>
                  </div>
                </React.Fragment>
              )}

              {useInAppScreenRecorder && (
                <React.Fragment>
                  {!hasStartedRecording && (
                    <div className="action-button">
                      {!hasTappedOnStart && (
                        <a
                          className="btn btn-primary"
                          onClick={() => setHasTappedOnStart(true)}
                          href="app://job-engine/start-screen-recording"
                        >
                          {getString("record_button", lang)}
                        </a>
                      )}
                      {hasTappedOnStart && (
                        <Spinner
                          className={"start-button-spinner"}
                          type="grow"
                        />
                      )}
                    </div>
                  )}

                  {hasStartedRecording && (
                    <React.Fragment>
                      {!hasStoppedRecording && (
                        <div className="action-button">
                          <a
                            className="btn btn-primary"
                            onClick={() => setHasStoppedRecording(true)}
                            href="app://job-engine/stop-screen-recording"
                          >
                            Stop Recording
                          </a>
                        </div>
                      )}

                      {hasStoppedRecording && (
                        <React.Fragment>
                          <div className="steps no-background-or-shadow">
                            <div className="step checkbox-step">
                              <div className="step-number">
                                <label className="custom-control control-checkbox">
                                  <input
                                    type="checkbox"
                                    id="finished-checkbox"
                                    checked={isFinished}
                                    onChange={() => setIsFinished(!isFinished)}
                                  />
                                  <div className="control-indicator" />
                                </label>
                              </div>
                              <div className="step-description">
                                <label htmlFor="finished-checkbox">
                                  I have finished recording the steps
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="action-button">
                            <a
                              className={
                                "btn btn-primary " +
                                (isActionButtonEnabled() ? "" : "disabled")
                              }
                              href="app://job-engine/next"
                            >
                              Upload
                            </a>
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          )}

          {instructions.capture_type === "twitter" && (
            <div className="action-button">
              {!hasTappedOnStart && (
                <a
                  className="btn btn-primary"
                  onClick={() => setHasTappedOnStart(true)}
                  href="app://job-engine/start-twitter-capture"
                >
                  Start
                </a>
              )}
              {hasTappedOnStart && (
                <Spinner className={"start-button-spinner"} type="grow" />
              )}
            </div>
          )}

          {instructions.capture_type === "android_screen_time_stats" && (
            <div className="action-button">
              {!hasTappedOnStart && (
                <a
                  className="btn btn-primary"
                  onClick={() => setHasTappedOnStart(true)}
                  href="app://job-engine/start-android-screen-time-stats-capture"
                >
                  Start
                </a>
              )}
              {hasTappedOnStart && (
                <Spinner className={"start-button-spinner"} type="grow" />
              )}
            </div>
          )}

            {instructions.capture_type === "google_takeout_data_portability" && (
                <div className="action-button">
                    {!hasTappedOnStart && (
                        <a
                            className="btn btn-primary"
                            onClick={() => setHasTappedOnStart(true)}
                            href="app://job-engine/start-google-takeout-data-portability-capture"
                        >
                            Start
                        </a>
                    )}
                    {hasTappedOnStart && (
                        <Spinner className={"start-button-spinner"} type="grow" />
                    )}
                </div>
            )}

          {instructions.footer_html && (
            <div
              className="footer"
              dangerouslySetInnerHTML={{ __html: instructions.footer_html }}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default DataTypeInstructions;
